import React, { useState, useEffect } from 'react'
import Calling from '../../api/Calling';
import { FaEdit, FaTrash, FaPlus, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import Navbar from './Navbar';
import Loader from '../../components/Loader';

const ContactOptions = () => {
    const [loader, setLoader] = useState(false);
    const [contactOptions, setContactOptions] = useState([]);
    const [formData, setFormData] = useState({ option: '' });
    const [editMode, setEditMode] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getContactOption();
            if (resp?.status) {
                setContactOptions(resp.data || []);
            } else {
                setError(resp?.message || 'Failed to fetch Help & Support options');
            }
        } catch (error) {
            console.error(error);
            setError('An error occurred while fetching data');
        } finally {
            setLoader(false);
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }

    const resetForm = () => {
        setFormData({ option: '' });
        setEditMode(false);
        setCurrentId(null);
        setError('');
        setSuccess('');
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoader(true);
        setError('');
        setSuccess('');

        if (!formData.option.trim()) {
            setError('Option name is required');
            setLoader(false);
            return;
        }

        try {
            let resp;
            if (editMode) {
                resp = await Calling.updateContactOption({ option: formData.option, id: currentId });
            } else {
                resp = await Calling.createContactOption(formData);
            }

            if (resp?.status) {
                setSuccess(resp.message || `Help & Support option ${editMode ? 'updated' : 'created'} successfully`);
                toast.success(resp.message || `Help & Support option ${editMode ? 'updated' : 'created'} successfully`);
                resetForm();
                fetchData();
            } else {
                setError(resp?.message || `Failed to ${editMode ? 'update' : 'create'} Help & Support option`);
                toast.error(resp?.message || `Failed to ${editMode ? 'update' : 'create'} Help & Support option`);
            }
        } catch (error) {
            console.error(error);
            setError(`An error occurred while ${editMode ? 'updating' : 'creating'} Help & Support option`);
            toast.error(`An error occurred while ${editMode ? 'updating' : 'creating'} Help & Support option`);
        } finally {
            setLoader(false);
        }
    }

    const handleEdit = (option) => {
        setFormData({ option: option.option });
        setEditMode(true);
        setCurrentId(option._id);
        setError('');
        setSuccess('');
    }

    const handleDelete = async (id) => {
        const content = `You are about to delete a Help & Support option. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (!check) return toast.error("Captcha answer is incorrect", { autoClose: 2000 });

        setLoader(true);
        try {
            const resp = await Calling.deleteContactOption({ id });
            if (resp?.status) {
                toast.success('Help & Support option deleted successfully');
                fetchData();
            } else {
                setError(resp?.message || 'Failed to delete Help & Support option');
                toast.error(resp?.message || 'Failed to delete Help & Support option');
            }
        } catch (error) {
            console.error(error);
            setError('An error occurred while deleting Help & Support option');
            toast.error('An error occurred while deleting Help & Support option');
        } finally {
            setLoader(false);
        }
    }

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 mb-2">
                <h1 className="text-xl md:text-2xl font-bold text-main-font w-auto">Help & Support Options</h1>
            </div>

            <div className='bg-white rounded-2xl shadow-lg overflow-hidden'>
                <Navbar selectState={'manage'} />
                <hr className='border-gray-200' />

                {/* Form Section */}
                <div className="p-6">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            {editMode ? (
                                <>
                                    <FaEdit className="mr-2 text-blue-600" />
                                    Edit Help & Support Option
                                </>
                            ) : (
                                <>
                                    <FaPlus className="mr-2 text-green-600" />
                                    Add New Help & Support Option
                                </>
                            )}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Option Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="option"
                                    value={formData.option}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    placeholder="Enter Help & Support option name"
                                />
                            </div>

                            {error && (
                                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md">
                                    <p className="font-medium">Error</p>
                                    <p className="text-sm">{error}</p>
                                </div>
                            )}

                            {success && (
                                <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-md">
                                    <p className="font-medium">Success</p>
                                    <p className="text-sm">{success}</p>
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    type="submit"
                                    className={`flex items-center justify-center px-6 py-3 rounded-lg text-white font-medium transition-all duration-200 ${loader
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : editMode
                                            ? 'bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg'
                                            : 'bg-green-600 hover:bg-green-700 shadow-md hover:shadow-lg'
                                        }`}
                                    disabled={loader}
                                >
                                    {loader ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Processing...
                                        </>
                                    ) : editMode ? (
                                        <>
                                            <FaEdit className="mr-2" />
                                            Update Option
                                        </>
                                    ) : (
                                        <>
                                            <FaPlus className="mr-2" />
                                            Add Option
                                        </>
                                    )}
                                </button>

                                {editMode && (
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="flex items-center justify-center px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-all duration-200"
                                        disabled={loader}
                                    >
                                        <FaTimes className="mr-2" />
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* Options List */}
                    <div className="bg-gray-50 rounded-2xl p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                            Existing Help & Support Options ({contactOptions.length})
                        </h3>

                        {loader && !contactOptions.length ? (
                            <div className="flex justify-center items-center h-32">
                                <Loader />
                            </div>
                        ) : contactOptions.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {contactOptions.map((option) => (
                                    <div
                                        key={option._id}
                                        className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-200"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <h4 className="font-medium text-gray-800 text-lg">
                                                    {option.option}
                                                </h4>
                                                <p className="text-sm text-gray-500 mt-1">
                                                    Help & Support option
                                                </p>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => handleEdit(option)}
                                                    className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                                                    title="Edit"
                                                >
                                                    <FaEdit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(option._id)}
                                                    className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                                    title="Delete"
                                                >
                                                    <FaTrash className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 bg-white rounded-xl">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                                    <FaPlus className="w-6 h-6 text-gray-400" />
                                </div>
                                <p className="text-gray-500 text-lg font-medium">No Help & Support options found</p>
                                <p className="text-gray-400 mt-2">Add your first Help & Support option to get started!</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ContactOptions